import { ApplicationRegion } from '../entities/catalog/application.region';
import { ApplicationEnvironment } from '../entities/catalog/application.environment';
import { z } from 'zod';

export const requiredEmail = z.string().trim().email();
export const requiredString = z.string().trim();
export const optionalString = z.string().trim().optional().nullable();
export const kubernetesResourceName = z.string().trim().min(1).regex(
    /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
    'Must be a valid Kubernetes resource name (lowercase alphanumeric with hyphens)'
);
export const optionalKubernetesResourceName = kubernetesResourceName.optional().nullable();

export enum SignupStatus {
    Started = 'started',
    EmailProvided = 'email_provided',
    EmailVerified = 'email_verified',
    FormCompleted = 'form_completed',
    ContactRequested = 'contact_requested',
    Queued = 'queued',
    Provisioning = 'provisioning',
    KubernetesReady = 'kubernetes_ready',
    DataSeeding = 'data_seeding',
    InvitingUsers = 'inviting_users',
    Ready = 'ready',
    Failed = 'failed',
    Expired = 'expired'
}

const DateSchema = z.union([
    z.date(),
    z.string().datetime()
]);

export const SignupFullSchema = z
    .object({
        _id: requiredString,
        emailDomain: requiredString,
        email: requiredEmail,
        sessionToken: requiredString,
        status: z.nativeEnum(SignupStatus),
        lastActivityAt: DateSchema,
        expiresAt: DateSchema,
        emailVerifiedAt: DateSchema.optional(),
        completedAt: DateSchema.optional(),
        createdAt: DateSchema,
        updatedAt: DateSchema,

        tenant: z
            .object({
                suggestedName: kubernetesResourceName,
                override: z.boolean().optional(),
                overrideName: optionalKubernetesResourceName,
                region: z.nativeEnum(ApplicationRegion),
                environment: z.nativeEnum(ApplicationEnvironment).optional(),
            })
            .strict(),

        company: z
            .object({
                _id: requiredString,
                name: requiredString,
            })
            .strict(),

        contact: z
            .object({
                _id: requiredString,
                firstName: requiredString,
                lastName: requiredString,
                email: requiredEmail,
                phone: optionalString,
            })
            .strict(),

        marketing: z
            .object({
                industry: optionalString,
                employees: optionalString, // keep as string to match interface
            })
            .strict(),

        provisioning: z
            .object({
                workerId: optionalString,
                startedAt: DateSchema.optional(),
                completedAt: DateSchema.optional(),
                attempts: z.number().optional(),
                lastError: optionalString,
                tenantId: optionalString,
                tenantUrl: optionalString,
                heartbeat: DateSchema.optional(),
                claimedAt: DateSchema.optional(),
            })
            .strict(),

        history: z.array(
            z.object({
                status: z.nativeEnum(SignupStatus),
                timestamp: DateSchema,
                message: optionalString,
            })
        ),

        meta: z
            .object({
                ip: optionalString,
                source: optionalString,
                campaign: optionalString,
                affiliate: optionalString,
                query: optionalString,
                castle: z.unknown().optional(), // or z.any()
                email: z.unknown().optional(), // or z.any()
            })
            .strict(),

        restriction: z
            .object({
                type: z.enum(['region_unavailable']),
                region: optionalString,
                detectedAt: DateSchema.optional(),
            })
            .strict()
            .optional(),

        contactRequest: z
            .object({
                message: requiredString,
                requestedAt: DateSchema,
                respondedAt: DateSchema.optional(),
                responseNote: optionalString,
            })
            .strict()
            .optional(),
    });

export type SignupFull = z.infer<typeof SignupFullSchema>;

const withTenantOverrideRule = <T extends z.ZodTypeAny>(schema: T) => {
    return schema.superRefine((val: any, ctx) => {
        if (val.tenant?.override && !val.tenant?.overrideName) {
            ctx.addIssue({
                path: ["tenant", "overrideName"],
                code: z.ZodIssueCode.custom,
                message: "overrideName is required when override is true",
            });
        }
    }) as T;
};

export const SignupSchema = withTenantOverrideRule(
    SignupFullSchema.omit({
        email: true,
        emailDomain: true,
        sessionToken: true,
        status: true,
        lastActivityAt: true,
        expiresAt: true,
        emailVerifiedAt: true,
        completedAt: true,
        createdAt: true,
        updatedAt: true,
        meta: true,
        provisioning: true,
        history: true,
    })
);

export const SignupForTrialRequestSchema = z.object({
    data: SignupSchema,
})

// If you want your runtime type to be driven by the schema:
export type Signup = z.infer<typeof SignupSchema>;
export type SignupForTrialRequest = z.infer<typeof SignupForTrialRequestSchema>;
export type SignupCompany = SignupFull['company'];
export type SignupContact = SignupFull['contact'];
export type SignupMarketing = SignupFull['marketing'];
export type SignupMeta = SignupFull['meta'];
export type SignupTenant = SignupFull['tenant'];
export type SignupProvisioning = SignupFull['provisioning'];
export type SignupHistory = SignupFull['history'][0];
export type SignupRestriction = SignupFull['restriction'];
export type SignupContactRequest = SignupFull['contactRequest'];


export type SignupForTrialResponse = {
    success: boolean;
    message: string;
    data: {
        tenantId: string;
        provisioningRequestId: string;
        estimatedDeploymentTime: number;
    }
}
